// services/apoliceService.ts
// Minimal, clean apolice service scaffold that queries /documentos
import { apiClient } from '../config/api';

export interface ApoliceData {
  id?: string;
  tipoDocumento?: string;
  numeroDocumento?: string; // may be the document/numero
  codigoProposta?: string; // proposta code
  numeroApolice?: string | null;
  numeroEndosso?: string | null;
  dataEmissao?: string; // ISO date
  dataValidade?: string | null;
  inicioVigencia?: string | null; // ISO date
  fimVigencia?: string | null; // ISO date
  premioLiquido?: number | null;
  comissao?: number | null;
  quantidadeParcelas?: number | null;
  cpfCnpjCliente?: string | null;
  cliente?: {
    id?: string;
    cpfCnpj?: string;
    nome?: string;
    [k: string]: any;
  } | null;
  seguradora?: { id?: string; nome?: string } | null;
  produto?: { id?: string; nome?: string } | null;
  status?: { id?: string; nome?: string } | null;
  situacao?: { id?: string; nome?: string } | null;
  empresa?: { id?: string; nome?: string } | null;
  [key: string]: any;
}

export interface ApoliceSearchParams {
  search?: string;
  page?: number;
  limit?: number;
  // New: support dotted notation fields and explicit order
  sort?: string; // legacy: e.g. 'emissao:desc'
  sortBy?: string; // e.g. 'cliente.nome' or 'dataEmissao' (preferred)
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  // New: filter parameters
  filters?: {
    statusId?: string;
    situacaoId?: string;
    seguradoraId?: string;
    produtoId?: string;
    empresaId?: string;
    dataEmissao?: {
      gte?: string;
      lte?: string;
    };
    fimVigencia?: {
      gte?: string;
      lte?: string;
    };
    inicioVigencia?: {
      gte?: string;
      lte?: string;
    };
  };
}

export interface ApoliceListResponse {
  data: ApoliceData[];
  page?: number;
  totalPages?: number;
  total?: number;
}

export const apoliceService = {
  async getById(id: string): Promise<ApoliceData> {
    const res = await apiClient.request<any>(`/documentos/${id}`);
    // A API retorna { success: true, data: { ... } }
    return res.data || res;
  },

  async getApolices(params: ApoliceSearchParams = {}, resource: string = '/documentos'): Promise<ApoliceListResponse> {
    const qp = new URLSearchParams();
    if (params.search) qp.set('search', params.search);
    if (params.page) qp.set('page', String(params.page));
    if (params.limit) qp.set('limit', String(params.limit));
  // Prefer explicit sortBy/sortOrder if provided (maps to backend query params)
  if (params.sortBy) qp.set('sortBy', params.sortBy);
  if (params.sortOrder) qp.set('sortOrder', params.sortOrder);
  // Backwards-compat: allow older single 'sort' param like 'emissao:desc'
  else if (params.sort) qp.set('sort', params.sort);
    if (params.dateFrom) qp.set('dateFrom', params.dateFrom);
    if (params.dateTo) qp.set('dateTo', params.dateTo);

    // Add filters to query parameters
    if (params.filters) {
      if (params.filters.statusId) qp.set('filters[statusId]', params.filters.statusId);
      if (params.filters.situacaoId) qp.set('filters[situacaoId]', params.filters.situacaoId);
      if (params.filters.seguradoraId) qp.set('filters[seguradoraId]', params.filters.seguradoraId);
      if (params.filters.produtoId) qp.set('filters[produtoId]', params.filters.produtoId);
      if (params.filters.empresaId) qp.set('filters[empresaId]', params.filters.empresaId);
      if (params.filters.dataEmissao?.gte) qp.set('filters[dataEmissao][gte]', params.filters.dataEmissao.gte);
      if (params.filters.dataEmissao?.lte) qp.set('filters[dataEmissao][lte]', params.filters.dataEmissao.lte);
      if (params.filters.fimVigencia?.gte) qp.set('filters[fimVigencia][gte]', params.filters.fimVigencia.gte);
      if (params.filters.fimVigencia?.lte) qp.set('filters[fimVigencia][lte]', params.filters.fimVigencia.lte);
      if (params.filters.inicioVigencia?.gte) qp.set('filters[inicioVigencia][gte]', params.filters.inicioVigencia.gte);
      if (params.filters.inicioVigencia?.lte) qp.set('filters[inicioVigencia][lte]', params.filters.inicioVigencia.lte);
    }

    // Se há termo de busca, usar endpoint específico de search
    // resource default is '/documentos' but caller can pass '/apolices' or other resource
    const baseEndpoint = params.search ? `${resource}/search` : resource;
    const endpoint = `${baseEndpoint}${qp.toString() ? `?${qp.toString()}` : ''}`;
    const res = await apiClient.request<any>(endpoint);

    // Normalize response shapes that backend might return
    const payload = res?.data ?? res?.docs ?? res ?? {};

    let items: ApoliceData[] = [];
    if (Array.isArray(payload)) items = payload as ApoliceData[];
    else if (Array.isArray((payload as any).data)) items = (payload as any).data;
    else if (Array.isArray((payload as any).docs)) items = (payload as any).docs;
    else {
      // try to find first array value
      const maybeArr = Object.values(payload).find((v) => Array.isArray(v));
      if (Array.isArray(maybeArr)) items = maybeArr as ApoliceData[];
    }

    // try to extract pagination metadata when provided by the backend
    let page: number | undefined;
    let totalPages: number | undefined;
    let total: number | undefined;

    if (res && typeof res === 'object') {
      // Primeiro, tenta extrair da raiz da resposta
      if (typeof (res as any).page === 'number') page = (res as any).page;
      if (typeof (res as any).totalPages === 'number') totalPages = (res as any).totalPages;
      if (typeof (res as any).total === 'number') total = (res as any).total;
      
      // Depois tenta extrair de res.pagination (formato do backend AIXA)
      if (res.pagination && typeof res.pagination === 'object') {
        if (!page && typeof (res.pagination as any).page === 'number') page = (res.pagination as any).page;
        if (!totalPages && typeof (res.pagination as any).totalPages === 'number') totalPages = (res.pagination as any).totalPages;
        if (!total && typeof (res.pagination as any).total === 'number') total = (res.pagination as any).total;
      }
      
      // Por último, tenta extrair de res.data
      if (!page && res.data && typeof (res.data as any).page === 'number') page = (res.data as any).page;
      if (!totalPages && res.data && typeof (res.data as any).totalPages === 'number') totalPages = (res.data as any).totalPages;
      if (!total && res.data && typeof (res.data as any).total === 'number') total = (res.data as any).total;
    }

    return { data: items, page, totalPages, total };
  },

  async getRenovacoes(page: number = 1, limit: number = 5): Promise<ApoliceListResponse> {
    const qp = new URLSearchParams();
    qp.set('page', String(page));
    qp.set('limit', String(limit));
    
    const endpoint = `/documentos/renovacoes?${qp.toString()}`;
    const res = await apiClient.request<any>(endpoint);
    
    let items: ApoliceData[] = [];
    if (Array.isArray(res?.data)) items = res.data;
    else if (Array.isArray(res)) items = res;
    
    // Extrai metadados de paginação
    let pageNum: number | undefined;
    let totalPages: number | undefined;
    let total: number | undefined;
    
    if (res && typeof res === 'object') {
      if (typeof res.page === 'number') pageNum = res.page;
      if (typeof res.totalPages === 'number') totalPages = res.totalPages;
      if (typeof res.total === 'number') total = res.total;
      
      if (res.pagination && typeof res.pagination === 'object') {
        if (!pageNum && typeof res.pagination.page === 'number') pageNum = res.pagination.page;
        if (!totalPages && typeof res.pagination.totalPages === 'number') totalPages = res.pagination.totalPages;
        if (!total && typeof res.pagination.total === 'number') total = res.pagination.total;
      }
    }
    
    return { data: items, page: pageNum, totalPages, total };
  },

  async getPropostas(page: number = 1, limit: number = 5): Promise<ApoliceListResponse> {
    const qp = new URLSearchParams();
    qp.set('page', String(page));
    qp.set('limit', String(limit));
    
    const endpoint = `/documentos/propostas?${qp.toString()}`;
    const res = await apiClient.request<any>(endpoint);
    
    let items: ApoliceData[] = [];
    if (Array.isArray(res?.data)) items = res.data;
    else if (Array.isArray(res)) items = res;
    
    // Extrai metadados de paginação
    let pageNum: number | undefined;
    let totalPages: number | undefined;
    let total: number | undefined;
    
    if (res && typeof res === 'object') {
      if (typeof res.page === 'number') pageNum = res.page;
      if (typeof res.totalPages === 'number') totalPages = res.totalPages;
      if (typeof res.total === 'number') total = res.total;
      
      if (res.pagination && typeof res.pagination === 'object') {
        if (!pageNum && typeof res.pagination.page === 'number') pageNum = res.pagination.page;
        if (!totalPages && typeof res.pagination.totalPages === 'number') totalPages = res.pagination.totalPages;
        if (!total && typeof res.pagination.total === 'number') total = res.pagination.total;
      }
    }
    
    return { data: items, page: pageNum, totalPages, total };
  },
};

// Funções auxiliares para extrair nomes de objetos relacionados
export const getApoliceProdutoNome = (apolice: ApoliceData): string => {
  if (typeof apolice.produto === 'string') {
    return apolice.produto;
  }
  if (apolice.produto && typeof apolice.produto === 'object' && apolice.produto.nome) {
    return apolice.produto.nome;
  }
  return '-';
};

export const getApoliceSeguradoraNome = (apolice: ApoliceData): string => {
  if (typeof apolice.seguradora === 'string') {
    return apolice.seguradora;
  }
  if (apolice.seguradora && typeof apolice.seguradora === 'object' && apolice.seguradora.nome) {
    return apolice.seguradora.nome;
  }
  return '-';
};

export const getApoliceClienteNome = (apolice: ApoliceData): string => {
  if (apolice.cliente && typeof apolice.cliente === 'object' && apolice.cliente.nome) {
    return apolice.cliente.nome;
  }
  if (apolice.tomador) {
    return apolice.tomador;
  }
  return '-';
};
